import { createClient } from '@supabase/supabase-js';

// 創建單一共享的 Supabase 客戶端實例
// 這樣可以避免多實例警告和登入狀態衝突
const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('缺少 Supabase 環境變數');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true, // 🔥 啟用自動刷新
    detectSessionInUrl: true,
    storageKey: 'supabase-auth',
  },
  global: {
    headers: {
      'X-Client-Info': 'mars-casino-admin',
    },
  },
});

// 🔥 新增：Token 刷新狀態追蹤
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;

// 🔥 新增：手動刷新 Token 的函數
export const refreshToken = async () => {
  // 如果正在刷新，返回現有的 Promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  
  refreshPromise = (async () => {
    try {
      console.log('🔄 開始刷新 Token...');
      
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('❌ Token 刷新失敗:', error);
        return { success: false, error };
      }
      
      if (data.session) {
        console.log('✅ Token 刷新成功');
        return { success: true, session: data.session };
      }
      
      return { success: false, error: new Error('無法獲取新 Session') };
    } catch (error) {
      console.error('❌ Token 刷新異常:', error);
      return { success: false, error };
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();
  
  return refreshPromise;
};

// 🔥 新增：檢查 Token 是否即將過期
export const checkTokenExpiry = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { needsRefresh: false, isExpired: true };
    }
    
    const expiresAt = session.expires_at;
    if (!expiresAt) {
      return { needsRefresh: false, isExpired: false };
    }
    
    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = expiresAt - now;
    
    // 如果 Token 在 5 分鐘內過期，需要刷新
    const needsRefresh = timeUntilExpiry < 300;
    const isExpired = timeUntilExpiry <= 0;
    
    console.log(`⏰ Token 狀態: ${needsRefresh ? '需要刷新' : '正常'} (剩餘 ${Math.floor(timeUntilExpiry / 60)} 分鐘)`);
    
    return { needsRefresh, isExpired, timeUntilExpiry };
  } catch (error) {
    console.error('❌ 檢查 Token 過期時間失敗:', error);
    return { needsRefresh: false, isExpired: false };
  }
};

// 🔥 新增：帶自動刷新的 API 請求包裝器
export const supabaseWithRefresh = async <T>(
  apiCall: () => Promise<T>
): Promise<T> => {
  try {
    // 檢查 Token 是否需要刷新
    const { needsRefresh, isExpired } = await checkTokenExpiry();
    
    if (isExpired) {
      console.log('⚠️ Token 已過期，嘗試刷新...');
      const result = await refreshToken();
      
      if (!result.success) {
        throw new Error('Token 已過期且刷新失敗');
      }
    } else if (needsRefresh) {
      console.log('🔄 Token 即將過期，預先刷新...');
      // 不等待刷新完成，繼續執行請求
      refreshToken().catch(err => {
        console.warn('⚠️ 背景刷新 Token 失敗:', err);
      });
    }
    
    // 執行 API 請求
    return await apiCall();
  } catch (error: any) {
    // 如果是 JWT 錯誤，嘗試刷新後重試
    if (error.code === 'PGRST301' || error.message?.includes('JWT')) {
      console.log('🔄 檢測到 JWT 錯誤，嘗試刷新後重試...');
      
      const result = await refreshToken();
      
      if (result.success) {
        // 刷新成功，重試請求
        return await apiCall();
      }
    }
    
    throw error;
  }
};

// 監聽認證狀態變化
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT') {
    // 清除所有本地存儲
    localStorage.removeItem('supabase-auth');
    console.log('👋 使用者已登出');
  } else if (event === 'TOKEN_REFRESHED') {
    console.log('✅ Token 已自動刷新');
  } else if (event === 'SIGNED_IN') {
    console.log('✅ 使用者已登入');
  }
});

// 🔥 新增：定期檢查 Token 狀態（每 4 分鐘檢查一次）
setInterval(async () => {
  const { needsRefresh } = await checkTokenExpiry();
  
  if (needsRefresh) {
    console.log('⏰ 定期檢查：Token 即將過期，開始刷新...');
    await refreshToken();
  }
}, 4 * 60 * 1000); // 每 4 分鐘檢查一次

export default supabase;
